/**
 * Normalize leave type labels from Dynamics (handles NBSP, spacing, "1-Sick Leave", casing).
 */
export function normalizeLeaveTypeLabel(label) {
  return (label ?? '').toString().replace(/\u00a0/g, ' ').trim().replace(/\s+/g, ' ');
}

/** Strip leading numeric prefixes such as "1-", "3 -", "12." */
export function canonicalLeaveTypeLabel(label) {
  const normalized = normalizeLeaveTypeLabel(label).toLowerCase();
  return normalized.replace(/^\d+\s*[-.:]?\s*/, '').trim();
}

const ATTACHMENT_REQUIRED_PATTERNS = [
  /^sick(\s+leave)?$/,
  /^maternity(\s+leave)?$/,
  /^bereavement(\s+leave)?$/,
];

const PAST_DATES_ALLOWED_PATTERNS = [
  /^sick(\s+leave)?$/,
  /^bereavement(\s+leave)?$/,
];

function leaveTypeLabelMatches(label, patterns) {
  const core = canonicalLeaveTypeLabel(label);
  if (!core) return false;
  return patterns.some((pattern) => pattern.test(core));
}

/**
 * True when this leave type label requires at least one attachment
 * (Sick Leave, Maternity, Bereavement — including variants like "1-Sick Leave", "SIck Leave").
 */
export function leaveTypeLabelRequiresAttachments(label) {
  return leaveTypeLabelMatches(label, ATTACHMENT_REQUIRED_PATTERNS);
}

/**
 * True when past calendar dates may be selected for this leave type
 * (Sick Leave and Bereavement Leave, including Dynamics prefixes/casing).
 */
export function leaveTypeLabelAllowsPastDates(label) {
  return leaveTypeLabelMatches(label, PAST_DATES_ALLOWED_PATTERNS);
}

export function normalizeHrCategoryName(name) {
  return (name ?? '').toString().replace(/\u00a0/g, ' ').trim();
}

export function isLeaveRequestCategory(selectedCategory) {
  return normalizeHrCategoryName(selectedCategory) === 'Leave Request';
}

export function isSalaryCertificateCategory(selectedCategory) {
  return normalizeHrCategoryName(selectedCategory) === 'Salary Certificate Request';
}

export function isTimeOffRequestCategory(selectedCategory) {
  return normalizeHrCategoryName(selectedCategory) === 'Time Off Request';
}

/** Admin categories that only need category + reason (no extra form sections). */
const REASON_ONLY_ADMIN_CATEGORIES = new Set([
  'work permit request',
  'experience certificate',
  'authorized signatures request',
  'new mobile line request',
  'paci change address request',
  'new recruitment request',
  'employment certificate',
  'salary certificate request',
]);

export function isReasonOnlyAdminCategory(selectedCategory) {
  const key = normalizeHrCategoryName(selectedCategory).toLowerCase();
  return REASON_ONLY_ADMIN_CATEGORIES.has(key);
}

/** True when the general Reason textarea is shown and must be filled by the user. */
export function isReasonFieldVisible(selectedCategory, onlyReason) {
  if (!selectedCategory) return false;
  if (onlyReason || isReasonOnlyAdminCategory(selectedCategory)) return true;
  if (isLeaveRequestCategory(selectedCategory)) return true;
  if (isTimeOffRequestCategory(selectedCategory)) return true;
  if (isSalaryCertificateCategory(selectedCategory)) return false;
  if (normalizeHrCategoryName(selectedCategory) === 'Monthly Pay Slip') return true;
  if (normalizeHrCategoryName(selectedCategory) === 'Location Transfer Request') return true;
  if (normalizeHrCategoryName(selectedCategory) === 'Leave Resume') return true;
  return false;
}

/** Reason sent to the API: user input when the field is visible, otherwise the category name. */
export function resolveHrRequestReason(selectedCategory, onlyReason, userReason) {
  const trimmed = String(userReason ?? '').trim();
  if (isReasonFieldVisible(selectedCategory, onlyReason)) {
    return trimmed;
  }
  return normalizeHrCategoryName(selectedCategory) || 'HR Request';
}

function selectedLeaveTypeMatchesAnyLabel(leaveTypes, leaveTypeId, matcher) {
  if (leaveTypeId == null || leaveTypeId === '') return false;
  const id = String(leaveTypeId);
  let type = leaveTypes.find((t) => String(t.id) === id);
  if (!type) {
    type = leaveTypes.find(
      (t) =>
        String(t.value ?? '') === id ||
        String(t.label ?? '') === id ||
        String(t.name ?? '') === id
    );
  }
  if (!type) {
    return matcher(id);
  }
  return [type.value, type.name, type.label].some(matcher);
}

export function selectedLeaveTypeRequiresAttachments(leaveTypes, leaveTypeId) {
  return selectedLeaveTypeMatchesAnyLabel(
    leaveTypes,
    leaveTypeId,
    leaveTypeLabelRequiresAttachments
  );
}

export function selectedLeaveTypeAllowsPastDates(leaveTypes, leaveTypeId) {
  return selectedLeaveTypeMatchesAnyLabel(
    leaveTypes,
    leaveTypeId,
    leaveTypeLabelAllowsPastDates
  );
}

/**
 * Collect missing required field labels for the HR request form (before submit).
 */
export function collectHrRequestFormErrors({
  selectedCategory,
  onlyReason,
  newLeave,
  leaveTypes,
  hrRequestFiles,
  dateTimeforTimeOff,
}) {
  const errors = [];
  if (!selectedCategory) {
    errors.push('Category');
    return errors;
  }

  const reasonRequired = isReasonFieldVisible(selectedCategory, onlyReason);

  if (reasonRequired && !String(newLeave?.reason ?? '').trim()) {
    errors.push('Reason');
  }

  if (onlyReason) {
    return errors;
  }

  if (isLeaveRequestCategory(selectedCategory)) {
    if (!newLeave?.leave_type_id) errors.push('Leave Type');
    if (!newLeave?.start_date) errors.push('Start Date');
    if (!newLeave?.end_date) errors.push('End Date');
    if (
      selectedLeaveTypeRequiresAttachments(leaveTypes, newLeave?.leave_type_id) &&
      (!hrRequestFiles || hrRequestFiles.length === 0)
    ) {
      errors.push('Attachments');
    }
    return errors;
  }

  if (isTimeOffRequestCategory(selectedCategory)) {
    if (!dateTimeforTimeOff?.time_from_date) errors.push('From Date');
    if (!dateTimeforTimeOff?.time_from_hour) errors.push('From Time');
    if (!dateTimeforTimeOff?.time_to_date) errors.push('To Date');
    if (!dateTimeforTimeOff?.time_to_hour) errors.push('To Time');
    return errors;
  }

  if (isSalaryCertificateCategory(selectedCategory)) {
    return errors;
  }

  if (selectedCategory === 'Monthly Pay Slip') {
    if (!newLeave?.payslip_from_date) errors.push('From Date');
    if (!newLeave?.payslip_to_date) errors.push('To Date');
    return errors;
  }

  if (selectedCategory === 'Location Transfer Request') {
    if (!String(newLeave?.reallocation_reason ?? '').trim()) errors.push('Reallocation Reason');
    if (!newLeave?.transfer_from_location) errors.push('Transfer From Location');
    if (!newLeave?.transfer_to_location) errors.push('Transfer To Location');
    if (!newLeave?.effective_from_date) errors.push('Effective From Date');
    if (!newLeave?.section) errors.push('Section');
    return errors;
  }

  if (selectedCategory === 'Leave Resume') {
    if (!newLeave?.resume_status) errors.push('Resume Status');
    if (!newLeave?.actual_resume_date) errors.push('Actual Resume Date');
    if (!newLeave?.leave_request_reference) errors.push('Leave Request Reference');
    return errors;
  }

  return errors;
}

export function formatHrRequestValidationMessage(errors) {
  if (!errors?.length) return '';
  if (errors.length === 1) {
    return `Please complete the required field: ${errors[0]}.`;
  }
  return `Please complete the following required fields:\n${errors.map((field) => `• ${field}`).join('\n')}`;
}
